import { Button, Modal } from '@/components/ui'
import type { FreightQuoteOption } from '../api/types'

export interface TenderConfirmModalProps {
  option: FreightQuoteOption | null
  odooPoName: string
  isPending: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * Logistics overhaul Phase 2b — explicit confirm-before-commit step for
 * booking a rate. Tender is a real financial commitment (SPI is on the
 * hook for the carrier's invoice once this succeeds); this is the human
 * checkpoint the plan calls for before `/tender` is ever called.
 */
export function TenderConfirmModal({
  option,
  odooPoName,
  isPending,
  onCancel,
  onConfirm,
}: TenderConfirmModalProps) {
  if (!option) return null
  return (
    <Modal
      open={option !== null}
      onOpenChange={(next) => !next && onCancel()}
      tone="warning"
      title="Tender this rate?"
      description={
        <>
          This books a real load with <strong>{option.carrier_name ?? option.provider}</strong>
          {option.carrier_scac ? ` (${option.carrier_scac})` : ''} for{' '}
          <strong>
            {option.currency} {option.total.toFixed(2)}
          </strong>{' '}
          against <strong>{odooPoName}</strong>. SPI is committed to this carrier&apos;s invoice
          once this succeeds — it cannot be undone from here.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={isPending} onClick={onConfirm}>
            Tender {option.currency} {option.total.toFixed(2)}
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <dt className="text-role-metadata text-tertiary">Service</dt>
          <dd className="m-0">{option.service_level ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-role-metadata text-tertiary">Transit</dt>
          <dd className="m-0">
            {option.transit_days != null ? `${option.transit_days} day(s)` : '—'}
          </dd>
        </div>
        {option.expires_at ? (
          <div className="col-span-2">
            <dt className="text-role-metadata text-tertiary">Quote expires</dt>
            <dd className="m-0">{option.expires_at}</dd>
          </div>
        ) : null}
      </dl>
    </Modal>
  )
}
